import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getInvoicesOptions,
	sendPaymentReminderMutation,
	sendPaymentLinkMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import type { Invoice } from "@/lib/client/types.gen";
import type { ColumnDef } from "@tanstack/react-table";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
} from "@/components/data-table";
import { normalizeString } from "@/lib/utils";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
	Bell,
	Send,
	Mail,
	MessageSquare,
	Clock,
	AlertTriangle,
	Receipt,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { fr } from "date-fns/locale";
import { useState, useMemo } from "react";

export const Route = createFileRoute("/admin/reminders/")({
	component: RemindersManagementPage,
});

function RemindersManagementPage() {
	const queryClient = useQueryClient();
	const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(
		new Set(),
	);
	const [sendingReminders, setSendingReminders] = useState(false);

	const { data: invoicesData, isLoading } = useQuery(
		getInvoicesOptions({} as any),
	);
	const invoices = (invoicesData as any as Invoice[]) || [];

	const sendReminder = useMutation({
		...sendPaymentReminderMutation(),
		onSuccess: () => {
			toast.success("Relance envoyée avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de l'envoi de la relance");
		},
	});

	const sendPaymentLink = useMutation({
		...sendPaymentLinkMutation(),
		onSuccess: () => {
			toast.success("Lien de paiement envoyé avec succès");
		},
		onError: () => {
			toast.error("Erreur lors de l'envoi du lien de paiement");
		},
	});

	const handleSendReminder = async (invoiceId: string) => {
		await sendReminder.mutateAsync({
			path: { id: invoiceId } as any,
		});
	};

	const handleSendPaymentLink = async (parentId: string) => {
		await sendPaymentLink.mutateAsync({
			path: { id: parentId } as any,
		});
	};

	const handleBulkReminders = async () => {
		if (selectedInvoices.size === 0) {
			toast.error("Veuillez sélectionner au moins une facture");
			return;
		}

		setSendingReminders(true);
		let successCount = 0;
		let errorCount = 0;

		for (const invoiceId of selectedInvoices) {
			try {
				await sendReminder.mutateAsync({
					path: { id: invoiceId } as any,
				});
				successCount++;
			} catch {
				errorCount++;
			}
		}

		setSendingReminders(false);
		setSelectedInvoices(new Set());

		toast.success(
			`Relances envoyées: ${successCount} succès${errorCount > 0 ? `, ${errorCount} échec(s)` : ""}`,
		);

		queryClient.invalidateQueries({ queryKey: ["invoices"] });
	};

	// Filtrer les factures nécessitant une relance
	const overdueInvoices =
		invoices?.filter((inv) => {
			if (inv.status === "PAID" || inv.status === "CANCELLED") return false;
			if (!inv.dueDate) return false;
			const daysOverdue = differenceInDays(new Date(), new Date(inv.dueDate));
			return daysOverdue > 0;
		}) || [];

	const needsFirstReminder = overdueInvoices.filter((inv) => {
		if (!inv.firstPaymentAttemptDate) return true;
		const daysSinceFirstAttempt = differenceInDays(
			new Date(),
			new Date(inv.firstPaymentAttemptDate),
		);
		const reminderSent = inv.status === 'FAILED'
			? (inv as any).paymentFailureReminderSent
			: (inv as any).overdueReminderSent;
		return daysSinceFirstAttempt >= 2 && !reminderSent;
	});

	const needsSecondReminder = overdueInvoices.filter((inv) => {
		if (!inv.firstPaymentAttemptDate) return false;
		const daysSinceFirstAttempt = differenceInDays(
			new Date(),
			new Date(inv.firstPaymentAttemptDate),
		);
		return (
			daysSinceFirstAttempt >= 7 &&
			inv.paymentRetryCount &&
			inv.paymentRetryCount >= 2
		);
	});

	const toggleInvoiceSelection = (invoiceId: string) => {
		const newSelection = new Set(selectedInvoices);
		if (newSelection.has(invoiceId)) {
			newSelection.delete(invoiceId);
		} else {
			newSelection.add(invoiceId);
		}
		setSelectedInvoices(newSelection);
	};

	const selectAll = () => {
		setSelectedInvoices(new Set(overdueInvoices.map((inv) => inv.id || "")));
	};

	const deselectAll = () => {
		setSelectedInvoices(new Set());
	};

	const columns = useMemo<ColumnDef<Invoice>[]>(
		() => [
			{
				id: "select",
				header: () => (
					<Checkbox
						checked={
							overdueInvoices.length > 0 &&
							selectedInvoices.size === overdueInvoices.length
						}
						onCheckedChange={(checked) => {
							if (checked) selectAll();
							else deselectAll();
						}}
					/>
				),
				cell: ({ row }) => (
					<Checkbox
						checked={selectedInvoices.has(row.original.id || "")}
						onCheckedChange={() =>
							toggleInvoiceSelection(row.original.id || "")
						}
					/>
				),
				enableSorting: false,
				enableHiding: false,
			},
			{
				accessorKey: "invoiceNumber",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Facture" />
				),
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<Receipt className="h-4 w-4 text-gray-400" />
						<span className="font-medium">
							#{row.original.invoiceNumber}
						</span>
					</div>
				),
				meta: { label: "Facture" },
			},
			{
				id: "parent",
				accessorFn: (row) =>
					`${row.parent?.lastName ?? ""} ${row.parent?.firstName ?? ""}`,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => (
					<div>
						<p className="font-medium">
							{row.original.parent?.firstName}{" "}
							{row.original.parent?.lastName}
						</p>
						<p className="text-xs text-gray-600">
							{row.original.parent?.email}
						</p>
						<p className="text-xs text-gray-600">
							{row.original.parent?.phone}
						</p>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.parent?.firstName ?? ""} ${row.original.parent?.lastName ?? ""}`
					);
					const reverseName = normalizeString(
						`${row.original.parent?.lastName ?? ""} ${row.original.parent?.firstName ?? ""}`
					);
					const email = normalizeString(row.original.parent?.email || "");
					const phone = normalizeString(row.original.parent?.phone || "");
					return (
						fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						email.includes(searchValue) ||
						phone.includes(searchValue)
					);
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
			{
				accessorKey: "remainingAmount",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Montant dû" />
				),
				cell: ({ row }) => (
					<div>
						<p className="font-bold text-red-600">
							{row.original.remainingAmount?.toFixed(2)} €
						</p>
						<p className="text-xs text-gray-600">
							sur {row.original.totalAmount?.toFixed(2)} €
						</p>
					</div>
				),
				meta: {
					label: "Montant dû",
					textValue: (row: Invoice) => `${row.remainingAmount?.toFixed(2)} €`,
				},
			},
			{
				id: "overdue",
				accessorFn: (row) =>
					row.dueDate
						? differenceInDays(new Date(), new Date(row.dueDate))
						: 0,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Retard" />
				),
				cell: ({ getValue }) => {
					const daysOverdue = getValue<number>();
					return (
						<Badge
							variant={
								daysOverdue > 7
									? "destructive"
									: daysOverdue > 3
										? "warning"
										: "secondary"
							}
						>
							{daysOverdue} jour(s)
						</Badge>
					);
				},
				meta: {
					label: "Retard",
					textValue: (row: Invoice) => {
						const days = row.dueDate ? differenceInDays(new Date(), new Date(row.dueDate)) : 0;
						return `${days} jour(s)`;
					},
				},
			},
			{
				id: "retries",
				accessorFn: (row) => row.paymentRetryCount || 0,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Tentatives" />
				),
				cell: ({ row }) => (
					<div className="space-y-1">
						<p className="text-sm">
							{row.original.paymentRetryCount || 0} essai(s)
						</p>
						{row.original.lastPaymentAttemptDate && (
							<p className="text-xs text-gray-600">
								Dernier:{" "}
								{format(
									new Date(row.original.lastPaymentAttemptDate),
									"dd/MM",
									{ locale: fr },
								)}
							</p>
						)}
					</div>
				),
				meta: { label: "Tentatives" },
			},
			{
				id: "reminderStatus",
				accessorFn: (row) => {
					const reminderSent = row.status === 'FAILED'
						? (row as any).paymentFailureReminderSent
						: (row as any).overdueReminderSent;
					return reminderSent ? "sent" : "pending";
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Statut relance" />
				),
				cell: ({ row }) => {
					const reminderSent = row.original.status === 'FAILED'
						? (row.original as any).paymentFailureReminderSent
						: (row.original as any).overdueReminderSent;
					return reminderSent ? (
						<Badge variant="outline" className="text-green-600">
							<Mail className="h-3 w-3 mr-1" />
							Envoyée
						</Badge>
					) : (
						<Badge variant="outline" className="text-yellow-600">
							<Clock className="h-3 w-3 mr-1" />
							En attente
						</Badge>
					);
				},
				meta: {
					label: "Statut relance",
					textValue: (row: Invoice) => {
						const reminderSent = row.status === 'FAILED'
							? (row as any).paymentFailureReminderSent
							: (row as any).overdueReminderSent;
						return reminderSent ? "Envoyée" : "En attente";
					},
				},
			},
			{
				id: "actions",
				header: () => <span>Actions</span>,
				cell: ({ row }) => (
					<div className="flex items-center gap-2">
						<Button
							size="sm"
							variant="outline"
							onClick={() => handleSendReminder(row.original.id || "")}
							disabled={sendReminder.isPending}
						>
							<Bell className="h-3 w-3 mr-1" />
							Relancer
						</Button>
						<Button
							size="sm"
							variant="outline"
							onClick={() =>
								handleSendPaymentLink(row.original.parent?.id || "")
							}
							disabled={sendPaymentLink.isPending}
						>
							<Send className="h-3 w-3" />
						</Button>
					</div>
				),
				enableSorting: false,
				enableHiding: false,
				meta: { label: "Actions" },
			},
		],
		[selectedInvoices, overdueInvoices.length, sendReminder.isPending, sendPaymentLink.isPending],
	);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold">Gestion des relances</h1>
				<p className="text-gray-600 mt-2">
					Envoyez des rappels de paiement aux parents
				</p>
			</div>

			{/* Statistiques */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Factures en retard
						</CardTitle>
						<Clock className="h-4 w-4 text-red-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-red-600">
							{overdueInvoices.length}
						</div>
						<p className="text-xs text-muted-foreground">
							Nécessitent une attention
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">1ère relance</CardTitle>
						<Bell className="h-4 w-4 text-yellow-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-yellow-600">
							{needsFirstReminder.length}
						</div>
						<p className="text-xs text-muted-foreground">À envoyer (J+2)</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">2ème relance</CardTitle>
						<MessageSquare className="h-4 w-4 text-orange-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-orange-600">
							{needsSecondReminder.length}
						</div>
						<p className="text-xs text-muted-foreground">À envoyer (J+7)</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Montant en retard
						</CardTitle>
						<AlertTriangle className="h-4 w-4 text-red-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-red-600">
							{overdueInvoices
								.reduce((acc, inv) => acc + (inv.remainingAmount || 0), 0)
								.toFixed(0)}{" "}
							€
						</div>
						<p className="text-xs text-muted-foreground">Total à recouvrer</p>
					</CardContent>
				</Card>
			</div>

			{/* Actions groupées */}
			{selectedInvoices.size > 0 && (
				<Alert>
					<AlertDescription className="flex items-center justify-between">
						<span>
							<strong>{selectedInvoices.size}</strong> facture(s)
							sélectionnée(s)
						</span>
						<div className="flex gap-2">
							<Button variant="outline" size="sm" onClick={deselectAll}>
								Désélectionner tout
							</Button>
							<Button
								size="sm"
								onClick={handleBulkReminders}
								disabled={sendingReminders}
							>
								<Send className="mr-2 h-4 w-4" />
								{sendingReminders ? "Envoi..." : "Envoyer les relances"}
							</Button>
						</div>
					</AlertDescription>
				</Alert>
			)}

			{/* Liste des factures en retard */}
			<Card>
				<CardHeader>
					<div className="flex justify-between items-center">
						<div>
							<CardTitle>Factures nécessitant une relance</CardTitle>
							<CardDescription>
								Factures en retard de paiement avec détails des relances
							</CardDescription>
						</div>
						{overdueInvoices.length > 0 && (
							<Button
								variant="outline"
								size="sm"
								onClick={
									selectedInvoices.size === overdueInvoices.length
										? deselectAll
										: selectAll
								}
							>
								{selectedInvoices.size === overdueInvoices.length
									? "Désélectionner tout"
									: "Sélectionner tout"}
							</Button>
						)}
					</div>
				</CardHeader>
				<CardContent>
					<DataTable
						columns={columns}
						data={overdueInvoices}
						emptyMessage="Aucune facture en retard"
						pagination={false}
						toolbar={(table) => (
							<DataTableToolbar
								table={table}
								showViewOptions={false}
								showReset={false}
								showExportExcel
								exportFilename="relances"
							/>
						)}
					/>
				</CardContent>
			</Card>

			{/* Informations sur le processus de relance */}
			<Card>
				<CardHeader>
					<CardTitle>Processus de relance automatique</CardTitle>
					<CardDescription>
						Chronologie des relances et actions automatiques
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-20 text-sm text-gray-600">
								J-3
							</div>
							<div className="flex-1">
								<p className="font-medium">Notification préalable</p>
								<p className="text-sm text-gray-600">
									SMS + Email envoyé automatiquement 3 jours avant la date de
									prélèvement
								</p>
							</div>
						</div>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-20 text-sm text-gray-600">
								Jour J
							</div>
							<div className="flex-1">
								<p className="font-medium">Tentative de paiement</p>
								<p className="text-sm text-gray-600">
									Prélèvement automatique via Stripe. Si échec, notification
									immédiate avec lien de paiement
								</p>
							</div>
						</div>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-20 text-sm text-gray-600">
								J+2
							</div>
							<div className="flex-1">
								<p className="font-medium">Nouvelle tentative</p>
								<p className="text-sm text-gray-600">
									Nouvelle tentative de prélèvement automatique sans
									notification
								</p>
							</div>
						</div>
						<div className="flex items-start gap-4">
							<div className="flex-shrink-0 w-20 text-sm text-gray-600">
								J+7
							</div>
							<div className="flex-1">
								<p className="font-medium">Relance finale</p>
								<p className="text-sm text-gray-600">
									SMS + Email de relance si toujours impayé. Ensuite, gestion
									manuelle
								</p>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
